import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RiTruckLine, RiCheckboxCircleLine, RiCloseLine, RiHistoryLine } from 'react-icons/ri';
import supplierService, { Supplier, SupplierForm } from '../../services/supplierService';
import codeGeneratorService from '../../services/codeGeneratorService';
import { useCreateSupplier, useUpdateSupplier } from '../../hooks/mutations/useSupplierMutations';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { usePermission } from '../../hooks/usePermission';
import { queryKeys } from '../../queryClient';
import { SUPPLIERS_PATH, supplierDetailUrl } from '../../utils/supplierLinks';
import { parseRouteId } from '../../utils/routeId';
import { showAlert } from '../../utils/alert';
import { mapFieldErrors, focusFirstInvalidField } from '../../utils/mapFieldErrors';
import ConfirmModal from '../../components/ui/ConfirmModal';
import SupplierFormFields from '../../components/supplier/SupplierFormFields';
import {
  emptySupplierForm,
  supplierToForm,
  validateSupplierForm,
  supplierIfMatch,
  SUPPLIER_FIELD_LABELS,
} from '../../components/supplier/supplierForm';
import {
  DetailPageLayout,
  DetailSection,
  RecordMetaCard,
  EntityActivityTimeline,
  useDetailNavigation,
} from '../../components/detail';

/** ข้อความไทยจาก envelope ของ backend (user_message → message → detail) */
const serverMessage = (error: any): string => {
  const raw = error?.response?.data?.error?.user_message ||
    error?.response?.data?.error?.message ||
    error?.response?.data?.detail || '';
  return typeof raw === 'string' ? raw : '';
};

/**
 * หน้าเพิ่ม/แก้ไขผู้จำหน่าย — `/master-data/suppliers/new` · `/master-data/suppliers/:id/edit`
 * (แทนโมดัลเพิ่ม/แก้ไขใน SupplierPage · มติ PO 28 ต.ค. 2026 · หน้าตาแบบ CompanyFormPage)
 *
 * ตรรกะบันทึกยกมาจาก handleSave ของโมดัลเดิมครบ: กันกดซ้ำ (ref · BUG-S795) · 422 field_errors → ใต้ช่อง ·
 * ชื่อซ้ำ → ใต้ช่องชื่อ · รหัสซ้ำ (สร้าง) → ออกรหัสใหม่แล้วให้กดอีกครั้ง · If-Match กันแก้ทับ (409 → โหลดค่าล่าสุด)
 */
const SupplierFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined;
  const supplierId = isEdit ? parseRouteId(id) : null;
  const { hasPermission } = usePermission();
  const { backTo, goTo } = useDetailNavigation(SUPPLIERS_PATH);
  // แสดงข้อความสำเร็จเอง (KD3 S2121 BUG-S2121-002) — เหมือนโมดัลเดิม
  const createSupplierMutation = useCreateSupplier({ skipSuccessMessage: true });
  const updateSupplierMutation = useUpdateSupplier();

  const [formData, setFormData] = useState<SupplierForm>(() => emptySupplierForm());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [codeError, setCodeError] = useState('');
  const [initialSnapshot, setInitialSnapshot] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const supplierQuery = useQuery({
    queryKey: [...queryKeys.suppliers.all, 'detail', supplierId ?? 0],
    queryFn: () => supplierService.getSupplierById(supplierId!),
    enabled: isEdit && supplierId !== null,
    // หน้าแก้ไขต้องได้ token If-Match ล่าสุดเสมอ — ไม่ใช้ cache เก่าจากหน้าดู
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const supplier: Supplier | undefined = supplierQuery.data;

  // ตั้งค่าฟอร์มครั้งเดียวต่อผู้จำหน่าย (refetch ภายหลังไม่ทับสิ่งที่ผู้ใช้กำลังพิมพ์)
  const initializedFor = useRef<string | null>(null);
  useEffect(() => {
    if (isEdit) {
      if (!supplier) return;
      const key = `${supplier.id}|${supplier.updated_at ?? ''}`;
      if (initializedFor.current === key) return;
      initializedFor.current = key;
      const f = supplierToForm(supplier);
      setFormData(f);
      setInitialSnapshot(JSON.stringify(f));
      return;
    }
    if (initializedFor.current === 'new') return;
    initializedFor.current = 'new';
    // ใช้ preview รหัสถัดไปแทนการสร้างรหัสจริง (รหัสจริงออกตอนบันทึก)
    codeGeneratorService
      .getNextCodePreview('supplier', true)
      .then((r) => r.next_code)
      .catch((error) => {
        console.error('Error getting next code preview:', error);
        return '';
      })
      .then((code) => {
        const f = emptySupplierForm(code);
        setFormData(f);
        setInitialSnapshot(JSON.stringify(f));
      });
  }, [isEdit, supplier]);

  const isDirty = !saved && initialSnapshot !== null && JSON.stringify(formData) !== initialSnapshot;
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isDirty);

  const cancelTo = isEdit && supplierId ? supplierDetailUrl(supplierId) : backTo;

  const isSubmittingRef = useRef(false); // BUG-S795-DBLSUBMIT
  const saving = createSupplierMutation.isPending || updateSupplierMutation.isPending;

  const leaveAfterSave = (savedId?: number) => {
    setSaved(true); // ปลดตัวกันออกจากหน้า ก่อนเปลี่ยนหน้า
    if (savedId && hasPermission('suppliers.view')) goTo(supplierDetailUrl(savedId), { replace: true });
    else goTo(backTo, { replace: true });
  };

  const handleSave = async () => {
    if (isSubmittingRef.current) return;

    const errors = validateSupplierForm(formData);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      const el = document.querySelector(`[name="${Object.keys(errors)[0]}"]`) as HTMLInputElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
      return;
    }

    isSubmittingRef.current = true;
    try {
      if (!isEdit) {
        const created = await createSupplierMutation.mutateAsync(formData);
        setCodeError('');
        // KD3 S2121 (BUG-S2121-002): แสดงเสมอ
        showAlert('สำเร็จ', 'สร้างผู้จำหน่ายสำเร็จ', 'success');
        leaveAfterSave(created?.id);
        return;
      }
      if (!supplier) return;
      const updated = await updateSupplierMutation.mutateAsync({
        id: supplier.id,
        data: formData,
        // BUG-S162-P1: last-seen updated_at → server ตอบ 409 ถ้ามีคนแก้ไปก่อน (ไม่เขียนทับเงียบ)
        ifMatch: supplierIfMatch(supplier),
      });
      leaveAfterSave(updated?.id ?? supplier.id);
    } catch (error: any) {
      console.error('Error saving supplier:', error);

      // P-S601-1: 409 stale-edit (useMutationWithRefresh แจ้งเตือนข้อความไทยแล้ว) → โหลดค่าล่าสุดลงฟอร์มใหม่
      // ห้ามคงค่าที่พิมพ์ไว้แล้วให้กดซ้ำด้วย token ใหม่ = เขียนทับงานของอีกคนโดยไม่เห็นว่าเขาแก้อะไร
      if (isEdit && error?.response?.status === 409) {
        initializedFor.current = null;
        supplierQuery.refetch();
        return;
      }

      // BUG-S1162-001: server 422 field_errors → inline (WCAG 3.3.1)
      const fieldErrMap = mapFieldErrors(error);
      if (fieldErrMap) {
        setFormErrors((prev) => ({ ...prev, ...fieldErrMap }));
        focusFirstInvalidField(fieldErrMap);
        return;
      }

      const errorMessage = serverMessage(error);
      // OBS-S1109-001 (S1113): ชื่อซ้ำ → error ใต้ช่องชื่อ
      if (errorMessage.includes('ชื่อ') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
        setFormErrors((prev) => ({ ...prev, name: errorMessage }));
        const nameEl = document.getElementsByName('name')[0] as HTMLInputElement | undefined;
        if (nameEl) nameEl.focus();
        return;
      }
      if (!isEdit && errorMessage.includes('รหัส') && errorMessage.includes('มีอยู่ในระบบแล้ว')) {
        // รหัสซ้ำ → ออกรหัสใหม่ เตือนใต้ช่อง รอผู้ใช้กดบันทึกอีกครั้ง (ไม่บันทึกอัตโนมัติ)
        const oldCode = formData.code;
        try {
          const newCode = await codeGeneratorService.generateSingleCode('supplier');
          setFormData((prev) => ({ ...prev, code: newCode }));
          setCodeError(`รหัส ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาบันทึกใหม่`);
        } catch (genError) {
          console.error('Error generating new code:', genError);
          setCodeError('ไม่สามารถสร้างรหัสใหม่ได้ กรุณาลองใหม่');
        }
        return;
      }
      // แก้ไข: toast จาก useMutationWithRefresh แสดงข้อความ server แล้ว · สร้าง: แสดงเอง (skipSuccessMessage)
      if (!isEdit) showAlert('เกิดข้อผิดพลาด', errorMessage || 'ไม่สามารถเพิ่มผู้จำหน่ายได้', 'error');
    } finally {
      isSubmittingRef.current = false; // reset ทุก exit path
    }
  };

  const title = isEdit ? 'แก้ไขผู้จำหน่าย' : 'เพิ่มผู้จำหน่าย';
  const breadcrumbs = useMemo(() => [
    { label: 'ซัพพลายเออร์', path: backTo },
    ...(isEdit && supplier ? [{ label: supplier.name, path: supplierDetailUrl(supplier.id) }] : []),
    { label: isEdit ? 'แก้ไข' : 'เพิ่มใหม่' },
  ], [backTo, isEdit, supplier]);

  const notFound = isEdit && supplierId === null ? { response: { status: 404 } } : null;

  return (
    <DetailPageLayout
      breadcrumbs={breadcrumbs}
      icon={<RiTruckLine />}
      title={title}
      recordId={isEdit ? supplier?.code || supplierId : undefined}
      subtitle={isEdit ? supplier?.name : 'กรอกข้อมูลผู้จำหน่าย (รหัสสร้างอัตโนมัติ)'}
      backTo={cancelTo}
      loading={(isEdit && supplierQuery.isLoading) || (initialSnapshot === null && !notFound && !supplierQuery.isError)}
      error={notFound || supplierQuery.error}
      notFoundMessage="ไม่พบผู้จำหน่ายที่ระบุ (อาจถูกลบไปแล้ว)"
      onRetry={() => supplierQuery.refetch()}
      overlays={
        <ConfirmModal
          isOpen={showModal}
          onClose={onCancelLeave}
          onConfirm={onConfirmLeave}
          pushHistoryOnOpen={false}
          title="ข้อมูลยังไม่ได้บันทึก"
          message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
          confirmText="ออกจากหน้านี้"
          cancelText="อยู่ต่อ"
          isDanger={true}
        />
      }
      sidebar={
        isEdit && supplier ? (
          <>
            <RecordMetaCard
              createdAt={supplier.created_at}
              createdBy={supplier.created_by}
              updatedAt={supplier.updated_at}
              updatedBy={supplier.updated_by}
            />
            <DetailSection title="ประวัติกิจกรรม" icon={<RiHistoryLine />}>
              <EntityActivityTimeline entityType="supplier" entityId={supplier.id} fieldLabels={SUPPLIER_FIELD_LABELS} />
            </DetailSection>
          </>
        ) : undefined
      }
    >
      <DetailSection title="ข้อมูลผู้จำหน่าย" icon={<RiTruckLine />}>
        <div>
          <SupplierFormFields
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
            codeError={codeError}
          />
          <div className="mt-6 pt-4 border-t border-secondary-200 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Link
              to={cancelTo}
              className="inline-flex items-center justify-center px-5 py-2.5 border border-secondary-300 rounded-lg text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 transition-all shadow-button"
            >
              <RiCloseLine className="w-4 h-4 mr-1.5" />
              ยกเลิก
            </Link>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all shadow-button disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RiCheckboxCircleLine className="w-4 h-4 mr-1.5" />
              {saving ? 'กำลังบันทึก...' : 'บันทึก'}
            </button>
          </div>
        </div>
      </DetailSection>
    </DetailPageLayout>
  );
};

export default SupplierFormPage;
